import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import PlayModal from "@/pages/detail/components/PlayModal";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);
  const [playOpen, setPlayOpen] = useState(false);

  usePageMeta({
    title: movie ? `影视详情 - ${movie.title}` : "影视详情 - 秋霞影院",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}，${movie.genre.join("/")}）：${movie.synopsis.slice(0, 80)}`
      : "秋霞影院影视详情页，查看影片基本信息、剧情简介与相关推荐。",
    keywords: movie
      ? `${movie.title},${movie.genre.join(",")},${movie.region}影视,秋霞影院`
      : "秋霞影院,影视详情",
  });

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .map((m) => {
        const tagScore = m.tags.filter((t) => movie.tags.includes(t)).length;
        const genreScore = m.genre.filter((g) => movie.genre.includes(g)).length;
        return { m, score: tagScore * 2 + genreScore };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || b.m.popularity - a.m.popularity)
      .slice(0, 4)
      .map((x) => x.m);
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影视资源不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">该影视作品可能已下架或链接有误，去看看其他精彩内容吧</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400"
          >
            浏览电影专区
          </Link>
        </div>
      </div>
    );
  }

  const metaRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genre.join(" / ") },
    { label: "年份", value: `${movie.year}` },
    { label: "地区", value: movie.region },
    { label: "时长", value: movie.duration },
  ];

  return (
    <div>
      <div className="relative w-full">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} - 秋霞影院影视详情`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-950/70" />

        <div className="relative mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-background-300" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${movie.type}`}
              className="cursor-pointer transition-colors hover:text-accent-400"
            >
              {movie.type === "movie" ? "电影" : "电视剧"}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">{movie.title}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-6 sm:flex-row">
            <div className="w-36 shrink-0 sm:w-44 md:w-52">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 秋霞影院`}
                className="aspect-[2/3] w-full rounded-lg object-cover object-top"
              />
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl font-bold text-white md:text-4xl">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-background-200">
                <span className="flex items-center gap-1 rounded bg-accent-500 px-2 py-0.5 font-semibold text-white">
                  <i className="ri-star-fill text-xs" /> {movie.rating.toFixed(1)}
                </span>
                <span>{movie.year}</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-fill text-accent-400" /> 热度 {(movie.popularity / 1000).toFixed(1)}k
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genre.map((g) => (
                  <Link
                    key={g}
                    to={`/search?q=${encodeURIComponent(g)}`}
                    className="cursor-pointer rounded-full border border-background-100/30 px-3 py-1 text-xs text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-lg" /> 立即播放
                </button>
                <Link
                  to={`/category/${movie.type}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 py-3 text-sm font-medium text-white transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  <i className="ri-list-check-2" /> 更多同类
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                本片由 {movie.director} 执导，{movie.cast.join("、")} 等联袂出演，于 {movie.releaseDate} 与观众见面，
                属于 {movie.genre.join("、")} 类型，片长 {movie.duration}。影片在叙事节奏与影像风格上都颇具辨识度，
                适合喜爱 {movie.genre[0]} 题材的观众细细品味。
              </p>
            </section>

            <section className="mt-6">
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
              {related.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {related.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-background-300 bg-background-50 py-10 text-center text-sm text-foreground-500">
                  暂无相关推荐
                </p>
              )}
            </section>
          </div>

          <aside className="space-y-6">
            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">作品资料</h2>
              <dl className="mt-4 space-y-3 text-sm">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">所属分类与标签</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/category/${movie.type}`}
                  className="cursor-pointer rounded-md bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                >
                  {movie.type === "movie" ? "电影专区" : "电视剧专区"}
                </Link>
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>

      <PlayModal open={playOpen} title={movie.title} onClose={() => setPlayOpen(false)} />
    </div>
  );
}